import React, { useState, useEffect, useMemo } from "react";
import { ArrowRightLeft } from "lucide-react";
import { convertValue, formatNumber } from "../lib/convert";

export const ConverterWidget = ({ category, initialFrom, initialTo }) => {
  const [from, setFrom] = useState(initialFrom || category.units[0].id);
  const [to, setTo] = useState(initialTo || category.units[1].id);
  const [value, setValue] = useState("1");

  useEffect(() => {
    if (initialFrom) setFrom(initialFrom);
    if (initialTo) setTo(initialTo);
  }, [initialFrom, initialTo]);

  const result = useMemo(() => {
    const v = parseFloat(value);
    if (Number.isNaN(v)) return NaN;
    return convertValue(category, from, to, v);
  }, [category, from, to, value]);

  const fromUnit = category.units.find((u) => u.id === from);
  const toUnit = category.units.find((u) => u.id === to);

  const swap = () => { setFrom(to); setTo(from); };

  return (
    <div className="border-2 border-ink bg-paper shadow-brutal" data-testid="converter-widget">
      <div className="grid sm:grid-cols-[1fr_auto_1fr] gap-0">
        {/* FROM */}
        <div className="p-6 border-b-2 sm:border-b-0 sm:border-r-2 border-ink">
          <label className="font-mono text-[11px] uppercase tracking-[0.2em] text-ink/50">From</label>
          <input
            data-testid="converter-input"
            type="number"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            className="w-full font-mono text-3xl sm:text-4xl font-bold bg-transparent outline-none mt-2 mb-4"
          />
          <select
            data-testid="converter-from-select"
            value={from}
            onChange={(e) => setFrom(e.target.value)}
            className="w-full font-mono text-sm border-2 border-ink bg-canvas px-3 py-2.5 outline-none cursor-pointer focus:border-signal"
          >
            {category.units.map((u) => (
              <option key={u.id} value={u.id}>{u.name} ({u.symbol})</option>
            ))}
          </select>
        </div>

        {/* SWAP */}
        <div className="flex items-center justify-center px-2 py-3 sm:py-0 border-b-2 sm:border-b-0 border-ink">
          <button
            data-testid="converter-swap"
            onClick={swap}
            aria-label="Swap units"
            className="w-12 h-12 border-2 border-ink bg-signal text-paper flex items-center justify-center shadow-brutalSm hover:rotate-180 transition-transform duration-300"
          >
            <ArrowRightLeft className="w-5 h-5" />
          </button>
        </div>

        {/* TO */}
        <div className="p-6 bg-canvas">
          <label className="font-mono text-[11px] uppercase tracking-[0.2em] text-ink/50">To</label>
          <div data-testid="converter-result" className="font-mono text-3xl sm:text-4xl font-bold mt-2 mb-4 text-signal break-all">
            {formatNumber(result)}
          </div>
          <select
            data-testid="converter-to-select"
            value={to}
            onChange={(e) => setTo(e.target.value)}
            className="w-full font-mono text-sm border-2 border-ink bg-paper px-3 py-2.5 outline-none cursor-pointer focus:border-signal"
          >
            {category.units.map((u) => (
              <option key={u.id} value={u.id}>{u.name} ({u.symbol})</option>
            ))}
          </select>
        </div>
      </div>
      <div className="border-t-2 border-ink px-6 py-3 font-mono text-xs text-ink/60">
        {value || "0"} {fromUnit?.symbol} = <span className="text-ink font-bold">{formatNumber(result)} {toUnit?.symbol}</span>
      </div>
    </div>
  );
};
